import type { VoiceCallUiStatus } from './use-voice-call';
import { endingSoonMinutes } from './voice-call-timer';

/**
 * Where the live call is being shown — full surface, or shrunk to the island —
 * as a pure state machine, deliberately outside React.
 *
 * The whole point of minimizing is that the CALL is untouched by it: audio,
 * transcript and tool calls keep running while the user goes and looks at
 * whatever they just asked for. So this machine only ever moves the presentation
 * (`minimized`); it has no way to end a call, and `startedAt` — the proof the
 * session was never restarted — survives every presentation change. Keeping it
 * pure is what lets the tests assert that directly instead of inspecting pixels.
 */

export type VoiceCallPresence = {
  status: VoiceCallUiStatus;
  minimized: boolean;
  /** Epoch ms of the call's start; null when there is no call. */
  startedAt: number | null;
  /** The server's ceiling for this call, from the mint response. */
  maxCallDurationMs: number | null;
};

export const IDLE_PRESENCE: VoiceCallPresence = {
  status: 'idle',
  minimized: false,
  startedAt: null,
  maxCallDurationMs: null,
};

export type PresenceEvent =
  | { type: 'start'; at: number }
  | { type: 'status'; status: VoiceCallUiStatus }
  | { type: 'cap'; maxCallDurationMs: number }
  | { type: 'minimize' }
  | { type: 'expand' }
  | { type: 'end' };

/** Connecting and live are the states with a call behind them. */
export function isCallActive(status: VoiceCallUiStatus): boolean {
  return status === 'connecting' || status === 'live';
}

export function reducePresence(state: VoiceCallPresence, event: PresenceEvent): VoiceCallPresence {
  switch (event.type) {
    case 'start':
      return { status: 'connecting', minimized: false, startedAt: event.at, maxCallDurationMs: null };
    case 'cap':
      // Set once, from the FIRST mint: the deadline is measured from the start of
      // the call, and a re-mint (pod cycle, reconnect, voice switch) must not push
      // the ceiling out from under the warning.
      return state.maxCallDurationMs != null ? state : { ...state, maxCallDurationMs: event.maxCallDurationMs };
    case 'status':
      // A call that stopped has nothing to minimize into, and a FAILURE must not be
      // hideable behind an island — the error screen is the only thing that offers
      // the dismiss.
      return {
        ...state,
        status: event.status,
        minimized: isCallActive(event.status) ? state.minimized : false,
      };
    case 'minimize':
      return isCallActive(state.status) ? { ...state, minimized: true } : state;
    case 'expand':
      return state.minimized ? { ...state, minimized: false } : state;
    case 'end':
      return IDLE_PRESENCE;
    default:
      return state;
  }
}

export type PresenceView = {
  /** The compact island: a live call the user has shrunk away. */
  islandVisible: boolean;
  /** The full call surface — also shown for a failure the user hasn't dismissed. */
  surfaceVisible: boolean;
  elapsedMs: number;
  endingSoonMinutes: number | null;
};

export function presenceView(state: VoiceCallPresence, nowMs: number): PresenceView {
  const active = isCallActive(state.status);
  const elapsedMs = state.startedAt == null ? 0 : Math.max(0, nowMs - state.startedAt);
  return {
    islandVisible: active && state.minimized,
    surfaceVisible: (active || state.status === 'error') && !state.minimized,
    elapsedMs,
    endingSoonMinutes: active ? endingSoonMinutes(elapsedMs, state.maxCallDurationMs) : null,
  };
}
